import { usePrevious } from 'ahooks';
import isEqual from 'lodash/isEqual';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { GroupedOptionsType } from 'react-select';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Squad } from 'api/models';
import { SquadValue } from 'components/molecules/squad-picker/index';
import { SquadPopover } from 'components/molecules/squad-picker/popover';
import { Picker } from './picker';
import { Option } from './types';
import { optionsToSquadPayload, squadToOptions } from './utils';

interface ChildrenProps {
  value: Option[];
}

export interface SquadPickerProps {
  value?: Squad;
  onChange?: (selected: SquadValue) => void;
  children: (props: ChildrenProps) => React.ReactNode;
  options?: GroupedOptionsType<Option>;
  visible?: boolean;
  onClose?: () => void;
  isDisabled?: boolean;
}

export const SquadPicker = forwardRef((props: SquadPickerProps, ref) => {
  const {
    value: givenValue = { users: [], teams: [] },
    onChange,
    children,
    options,
    visible,
    onClose,
    isDisabled,
  } = props;

  /**
   * ahooks usePrevious doesn't work with props, and works for state alone
   * hence this unnecessary state.
   */
  const [visibility, setVisibility] = useState(visible);

  useEffect(() => {
    setVisibility(visible);
  }, [visible]);
  const lastVisibility = usePrevious(visibility);

  const squadOptions = squadToOptions(givenValue);

  const [internalState, setInternalState] = useState<Option[]>(squadOptions);

  useDeepCompareEffect(() => {
    setInternalState(squadToOptions(givenValue));
  }, [givenValue]);

  const onSelectChange = (selected) => {
    setInternalState(selected ?? []);
  };

  const commit = useCallback(() => {
    if (onChange) {
      if (!isEqual(squadOptions, internalState)) {
        onChange(optionsToSquadPayload(internalState));
      }
    }
    onClose?.();
  }, [internalState, onChange, onClose, squadOptions]);

  /**
   * If visible prop is passed by the consumer to control the visibility,
   * it will be passed to the popover component,
   * else it will be ignored for the default trigger and dismiss to work.
   */
  const _props = {} as Partial<SquadPickerProps>;

  if (visible !== undefined) {
    _props.visible = visible;
  }

  /**
   * When visible is updated to false to hide the popover,
   * onVisibleChange is not triggered, hence the current selection is not saved
   * hence we commit the changes, when visible is set to false by the consumer.
   */
  useEffect(() => {
    if (lastVisibility === true && visible === false) {
      commit();
    }
  }, [commit, lastVisibility, visible]);

  return (
    <SquadPopover
      {..._props}
      ref={ref}
      onClose={commit}
      content={
        <Picker
          onChange={onSelectChange}
          value={internalState}
          options={options}
          isDisabled={isDisabled}
        />
      }
    >
      {children ? children({ value: internalState }) : null}
    </SquadPopover>
  );
});
